import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";

const onlyDigits = (value = "") => value.replace(/\D/g, "");

const formatCnpj = (value = "") => {
  const digits = onlyDigits(value).slice(0, 14);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const formatCep = (value = "") => {
  const digits = onlyDigits(value).slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

export default function CompanyProfile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const lastCepRequestedRef = useRef("");
  const [formData, setFormData] = useState({
    name: "",
    trade_name: "",
    cnpj: "",
    state_registration: "",
    email: "",
    phone: "",
    address: "",
    address_number: "",
    neighborhood: "",
    city: "",
    state: "",
    zip_code: "",
    city_code: "",
    nfce_id_csc: "",
    nfce_csc: "",
  });

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/company/profile`);
      const company = response.data || {};
      const settings = company.settings || {};
      const nfceSettings = settings.nfce || {};

      setFormData({
        name: company.name || "",
        trade_name: company.trade_name || settings.trade_name || "",
        cnpj: formatCnpj(company.cnpj || ""),
        state_registration: company.state_registration || settings.state_registration || "",
        email: company.email || "",
        phone: company.phone || "",
        address: company.address || "",
        address_number: company.address_number || settings.address_number || "",
        neighborhood: company.neighborhood || settings.neighborhood || "",
        city: company.city || settings.city || "",
        state: (company.state || settings.state || "").toUpperCase(),
        zip_code: formatCep(company.zip_code || settings.zip_code || ""),
        city_code: onlyDigits(company.city_code || settings.city_code || "").slice(0, 7),
        nfce_id_csc: onlyDigits(company.nfce_id_csc || nfceSettings.id_csc || settings.nfce_id_csc || "").slice(0, 20),
        nfce_csc: company.nfce_csc || nfceSettings.csc || settings.nfce_csc || "",
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Erro ao carregar dados da empresa");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    if (name === "cnpj") finalValue = formatCnpj(value);
    if (name === "zip_code") {
      const cepDigits = onlyDigits(value).slice(0, 8);
      finalValue = formatCep(value);

      if (cepDigits.length === 8) {
        void fillAddressByCep(cepDigits);
      } else {
        lastCepRequestedRef.current = "";
      }
    }
    if (name === "city_code") finalValue = onlyDigits(value).slice(0, 7);
    if (name === "state") finalValue = value.toUpperCase().slice(0, 2);
    if (name === "nfce_id_csc") finalValue = onlyDigits(value).slice(0, 20);

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));
  };

  const fillAddressByCep = async (cepDigits) => {
    if (cepDigits.length !== 8 || lastCepRequestedRef.current === cepDigits) {
      return;
    }

    lastCepRequestedRef.current = cepDigits;

    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cepDigits}/json/`);
      const viaCep = response.data;

      if (viaCep?.erro) return;

      setFormData((prev) => ({
        ...prev,
        address: viaCep.logradouro || prev.address,
        neighborhood: viaCep.bairro || prev.neighborhood,
        city: viaCep.localidade || prev.city,
        state: (viaCep.uf || prev.state || "").toUpperCase().slice(0, 2),
        city_code: onlyDigits(viaCep.ibge || prev.city_code).slice(0, 7),
      }));
    } catch (_error) {
      // Falha do ViaCEP não deve bloquear o preenchimento manual
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/company/profile`, {
        ...formData,
        cnpj: onlyDigits(formData.cnpj),
        zip_code: onlyDigits(formData.zip_code),
        city_code: onlyDigits(formData.city_code),
        nfce_id_csc: onlyDigits(formData.nfce_id_csc),
        nfce_csc: formData.nfce_csc,
      });
      toast.success("Dados da empresa atualizados com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Erro ao atualizar dados da empresa");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
        Dados da Empresa
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Preencha corretamente os dados fiscais para emissão de NF-e.
      </Typography>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(4, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            <TextField
              label="Razão Social"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
              sx={{ gridColumn: { xs: "1 / -1", md: "span 2" } }}
            />
            <TextField
              label="Nome Fantasia"
              name="trade_name"
              value={formData.trade_name}
              onChange={handleChange}
              fullWidth
              sx={{ gridColumn: { xs: "1 / -1", md: "span 2" } }}
            />

            <TextField
              label="CNPJ"
              name="cnpj"
              value={formData.cnpj}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Inscrição Estadual (IE)"
              name="state_registration"
              value={formData.state_registration}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Telefone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              fullWidth
            />

            <TextField
              label="Logradouro"
              name="address"
              value={formData.address}
              onChange={handleChange}
              fullWidth
              sx={{ gridColumn: { xs: "1 / -1", md: "span 2" } }}
            />
            <TextField
              label="Número"
              name="address_number"
              value={formData.address_number}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Bairro"
              name="neighborhood"
              value={formData.neighborhood}
              onChange={handleChange}
              fullWidth
            />

            <TextField
              label="Cidade"
              name="city"
              value={formData.city}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="UF"
              name="state"
              value={formData.state}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="CEP"
              name="zip_code"
              value={formData.zip_code}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Cód. Município IBGE"
              name="city_code"
              value={formData.city_code}
              onChange={handleChange}
              fullWidth
              helperText="Ex.: 5003702"
            />
            <TextField
              label="NFC-e ID CSC"
              name="nfce_id_csc"
              value={formData.nfce_id_csc}
              onChange={handleChange}
              fullWidth
              helperText="Identificador do CSC na SEFAZ (ex.: 1)"
            />
            <TextField
              label="NFC-e CSC"
              name="nfce_csc"
              value={formData.nfce_csc}
              onChange={handleChange}
              fullWidth
              type="password"
              sx={{ gridColumn: { xs: "1 / -1", md: "span 2" } }}
              helperText="Token CSC da NFC-e fornecido pela SEFAZ"
            />

            <Box sx={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
              >
                {saving ? "Salvando..." : "Salvar Dados da Empresa"}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
